import {observable, action, computed, makeObservable} from 'mobx';
import * as Sentry from '@sentry/react';

import FeedbackContentForm from 'stores/forms/feedback-content-form';
import store from 'stores/store';
import {FEEDBACK_PARENT_TYPE_ENUM} from '@swipe-guide/clients-shared/enums';
import {posthogCapture} from '@swipe-guide/clients-shared/utils/posthog-utils';

class FeedbackStore {
  @observable opened = false;
  @observable sending = false;
  @observable saveLabel = '';
  @observable savingLabel = '';
  @observable wrapperRef = null;
  form = FeedbackContentForm();

  @observable availableCallers = [];
  @observable unavailableCallers = [];
  @observable callersAreLoading = false;
  @observable callersError = null;
  @observable fullName = '';

  @action
  setFullName = fullName => (this.fullName = fullName);

  @action
  setCallersAreLoading = callersAreLoading => (this.callersAreLoading = callersAreLoading);

  @action
  setCallersError = callersError => {
    if (callersError) {
      Sentry.captureException(callersError);
    }

    this.callersError = callersError;
  };

  @action
  setWrapperRef = node => {
    this.wrapperRef = node;
  };

  @action toggleFeedback = () => {
    if (this.disableFeedback) {
      return false;
    }
    this.opened = !this.opened;
    posthogCapture('Open Feedback Dialog');
  };

  @action checkRecaptcha = execute => {
    this.sending = true;
    execute();
  };

  @action setSaveLabels = (saveLabel, savingLabel) => {
    this.saveLabel = saveLabel;
    this.savingLabel = savingLabel;
  };

  @action
  reset = () => {
    this.sending = false;
    this.opened = false;
    this.form.clear();
  };

  sendFeedback = async (recaptchaRef, createFeedbackMutation, successMessage, errorMessage) => {
    const {
      notification: {success, error}
    } = store;
    this.sending = true;
    try {
      const {text} = this.form.values();
      const feedback = {...this.getFeedbackObject(), body: text, user: this.fullName};
      const {errors} = await createFeedbackMutation({feedback});
      if (errors) {
        this.reset();
        error(errorMessage);
      } else {
        this.reset();
        success(successMessage);
        posthogCapture('Submit Feedback');
      }
    } catch (e) {
      this.reset();
      error(errorMessage);
    }
    if (recaptchaRef) {
      recaptchaRef.reset();
    }
  };

  getFeedbackObject = () => {
    const {
      router: {params}
    } = store;
    const {guideSlug, instructionSlug, stepId} = params;
    const {instructionPage, guidePage} = store;

    if (stepId) {
      return {
        parentType: FEEDBACK_PARENT_TYPE_ENUM.STEP,
        parentId: instructionPage.lastVisitedStep.id
      };
    } else if (instructionSlug) {
      return {
        parentType: FEEDBACK_PARENT_TYPE_ENUM.INSTRUCTION,
        parentId: instructionPage.currentInstruction.id
      };
    } else if (guideSlug) {
      return {
        parentType: FEEDBACK_PARENT_TYPE_ENUM.GUIDE,
        parentId: guidePage.currentGuideId
      };
    } else {
      return {};
    }
  };

  constructor() {
    makeObservable(this);
  }

  @computed get sendButtonText() {
    return this.saveLabel;
  }

  @computed get disableFeedback() {
    const {
      router: {
        currentRoute: {id: routeId}
      },
      instructionPage: {lastVisitedStep, currentInstruction},
      guidePage: {currentGuideId}
    } = store;

    const isGuideRoute = ['guide', 'embeddedGuide'].includes(routeId);

    if (isGuideRoute) {
      return !currentGuideId;
    }

    const isInstructionRoute = ['instruction', 'embeddedInstruction'].includes(routeId);
    if (isInstructionRoute) {
      return !currentInstruction;
    }

    const isStepRoute = ['step', 'embeddedStep'].includes(routeId);
    if (isStepRoute) {
      return ['nextInstruction', 'campaign'].includes(lastVisitedStep?.id);
    }

    return true;
  }
}

export default FeedbackStore;
